import { useLayoutEffect, useRef } from "react";

const TOP_OFFSET_SELECTORS = [
  "[data-app-header]",
  "[data-breadcrumbs]",
];

/**
 * Measures the height of fixed header elements and publishes it via a CSS variable.
 * Keeps responsive layouts in sync with sticky chrome.
 */
export const usePublishTopOffset = () => {
  const observersRef = useRef<ResizeObserver[] | null>(null);

  useLayoutEffect(() => {
    const collectNodes = () => {
      const nodes = new Set<HTMLElement>();
      TOP_OFFSET_SELECTORS.forEach((selector) => {
        document
          .querySelectorAll<HTMLElement>(selector)
          .forEach((el) => nodes.add(el));
      });
      return Array.from(nodes);
    };

    const updateOffset = () => {
      const nodes = collectNodes();
      const totalHeight =
        nodes.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0) ||
        0;
      document.documentElement.style.setProperty(
        "--app-top-offset",
        `${Math.round(totalHeight)}px`,
      );
    };

    updateOffset();

    const resizeObservers = collectNodes().map((node) => {
      const observer = new ResizeObserver(() => updateOffset());
      observer.observe(node);
      return observer;
    });
    observersRef.current = resizeObservers;

    const handleResize = () => updateOffset();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      observersRef.current?.forEach((observer) => observer.disconnect());
      observersRef.current = null;
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);
};
